'use client'
import Comparator from "@/app/components/auctor/comparator/Comparator";
import { getComparatorFiltersConfig, getComparatorResultsData, getComparatorTemplateConfig } from "@/app/endpoints/auctorEndpoints";
import { IFiltersConfigPayload } from "@/app/models/auctor";
import { useEffect, useState } from "react";

interface ComparatorEmbedProps {
    comparatorId: string;
    showFiltersAbove?: boolean;
    maxResults?: number;
    maxFiltersMobile?: number;
}

export default function ComparatorEmbed({
    comparatorId,
    showFiltersAbove = false,
    maxResults,
    maxFiltersMobile = 2,
}: ComparatorEmbedProps) {
    const [templateConfig, setTemplateConfig] = useState<any>(null);
    const [filtersConfig, setFiltersConfig] = useState<IFiltersConfigPayload | null>(null);
    const [initialResults, setInitialResults] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadComparatorData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const comparatorIdNum = parseInt(comparatorId);
                if (isNaN(comparatorIdNum)) {
                    throw new Error(`Invalid comparator ID: ${comparatorId}`);
                }

                // Charger les configurations et données en parallèle
                const [filtersConfigData, templateConfigData, resultsData] = await Promise.all([
                    getComparatorFiltersConfig(comparatorIdNum),
                    getComparatorTemplateConfig(comparatorIdNum),
                    getComparatorResultsData(comparatorIdNum)
                ]);

                setFiltersConfig(filtersConfigData);
                setTemplateConfig(templateConfigData.template_config);

                // Limiter les résultats si maxResults est spécifié
                const limitedResults = maxResults ? resultsData.slice(0, maxResults) : resultsData;
                setInitialResults(limitedResults);

            } catch (err) {
                console.error('Error loading comparator data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load comparator data');
            } finally {
                setIsLoading(false);
            }
        };

        loadComparatorData();
    }, [comparatorId, maxResults]);

    if (isLoading) {
        return (
            <div className="w-full bg-blue-100 text-blue-900 py-8 px-4 rounded-lg animate-pulse">
                <div className="flex justify-center items-center">
                    <div className="text-lg font-medium">Chargement du comparateur...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-red-100 text-red-900 py-4 px-4 rounded-lg border border-red-300">
                <div className="flex justify-center items-center">
                    <div className="text-sm">
                        <strong>Une erreur est survenue lors de la récupération des données du comparateur.</strong>
                    </div>
                </div>
            </div>
        );
    }

    if (!templateConfig || !filtersConfig) {
        return (
            <div className="w-full bg-yellow-100 text-yellow-900 py-4 px-4 rounded-lg border border-yellow-300">
                <div className="flex justify-center items-center">
                    <div className="text-sm">
                        Comparateur non disponible
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-8 w-full">
            <Comparator
                comparatorId={parseInt(comparatorId)}
                templateConfig={templateConfig}
                filtersConfig={filtersConfig}
                initialResults={initialResults}
                showFiltersAbove={showFiltersAbove}
                maxFiltersMobile={maxFiltersMobile}
                isPreview={false}
            />
        </div>
    );
}